import { compareAsc, differenceInHours, differenceInMilliseconds, differenceInMinutes, format, getMinutes, isBefore } from "date-fns";
import { BlockConfigurationObject } from "../interfaces/block-configuration-object.interface";
import { MatchLiveData } from "../interfaces/match-live-data.interface";
import { Match } from "../interfaces/match.interface";
import { GeneralService } from "./general.service";

export class MatchService {
	_configurationObject: BlockConfigurationObject;
	_nthFilter: (number: number) => string;
	_generalService: GeneralService;
	userRetrievedLat: number;
	userRetrievedLon: number;

	teamProximityThreshold: number;

	geoCountry: string;
	geoRegion: string;

	/* @ngInject */
	constructor(configurationObject: BlockConfigurationObject, nthFilter: (number: number) => string, generalService: GeneralService) {
		this._configurationObject = configurationObject;
		this._nthFilter = nthFilter;
		this._generalService = generalService;

		this.userRetrievedLat = null;
		this.userRetrievedLon = null;
		this.teamProximityThreshold = 100e3; // A team must be based within 100km of the user to qualify as "nearby"

		this.geoCountry = "";
		this.geoRegion = "";
	}

	matchIsLive(match: Match): boolean {
		return match && match.LiveData
			&& (match.LiveData.status === "live" || match.LiveData.status === "inprogress" || match.LiveData.status === "halftime")
			&& !this.isPreMatchOnly(match);
	}

	matchIsPaused(match: Match): boolean {
		return match && match.LiveData
			&& (false) // TODO add types of delay, including weather etc
			&& !this.isPreMatchOnly(match);
	}

	matchIsOver(match: Match): boolean {
		return match && match.LiveData 
			&& (match.LiveData.status === "completed" || match.LiveData.status === "ended" || match.LiveData.status === "retirement")
			&& !this.isPreMatchOnly(match);
	}

	matchIsNotOver(match: Match): boolean {
		return this.matchIsLive(match) || this.matchIsPaused(match) || this.matchIsUpcoming(match);
	}

	matchIsUpcoming(match: Match): boolean {
		if (!match) {
			return false;
		}

		if (this.isPreMatchOnly(match)) {
			return isBefore(new Date(), match.MatchData.UserDate);
		}

		return !match.LiveData || !match.LiveData.status;
	}

	isPreMatchOnly(match: Match): boolean {
		if (!match) {
			return true;
		}
		if (match.LiveData && match.LiveData.error) {
			return true;
		}
		const sportCode = match.MatchData.Sport.toUpperCase();
		return !this._configurationObject.liveSportsAvailable || !this._configurationObject.liveSportsAvailable.includes(sportCode);
	}

	matchStartsWithinHour(match: Match): boolean {
		if (!match) {
			return false;
		}
		if (this.isPreMatchOnly(match)) {
			return differenceInMinutes(new Date(), match.MatchData.UserDate) > -60
				&& isBefore(new Date(), match.MatchData.UserDate);
		}

		return match && (!match.LiveData || !match.LiveData.status) &&
			differenceInMinutes(new Date(), match.MatchData.UserDate) > -60;
	}

	hasHighlightedTeam(match: Match): boolean {
		return this._configurationObject.highlightedTeams
		&& this._configurationObject.highlightedTeams[match.MatchData.Sport.toUpperCase()]
		&& (this._configurationObject.highlightedTeams[match.MatchData.Sport.toUpperCase()].includes(match.MatchData.HomeTeam.Abv)
			|| this._configurationObject.highlightedTeams[match.MatchData.Sport.toUpperCase()].includes(match.MatchData.AwayTeam.Abv));
	}

	/**
	 * Takes in the latitide and longitude of two points, in decimal degrees, and returns an estimated distance between them in metres
	 */
	coordinateDistance(aLat: number, aLon: number, bLat: number, bLon: number): number {
		// method adapted from https://www.movable-type.co.uk/scripts/latlong.html
		const R = 6371e3; // metres
		const aLatRad = aLat * Math.PI / 180; // latitide of point a in radians
		const bLatRad = bLat * Math.PI / 180; // latitide of point b in radians
		const deltaLatRad = (bLat - aLat) * Math.PI / 180; // difference between latitudes in radians
		const deltaLonRad = (bLon - aLon) * Math.PI / 180; // difference between longitudes in radians

		const a = Math.sin(deltaLatRad / 2) * Math.sin(deltaLatRad / 2) +
			Math.cos(aLatRad) * Math.cos(bLatRad) * Math.sin(deltaLonRad/2) * Math.sin(deltaLonRad/2);
		
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

		return R * c;
	}

	isHighlightedMatch(m: Match): boolean {
		return this._configurationObject.highlightedMatches && this._configurationObject.highlightedMatches.includes(m.MatchData.SIMatchID);
	}

	gameDisplaySort(a: Match, b: Match): number {
		// if one game is highlighted and the other isn't, put the highlighted game ahead
		if (this.isHighlightedMatch(a) && !this.isHighlightedMatch(b)) {
			return -1;
		}

		if (!this.isHighlightedMatch(a) && this.isHighlightedMatch(b)) {
			return 1;
		}

		// if one game has a highlighted team in it and the other doesn't, put the game with the highlighted team ahead
		if (this.hasHighlightedTeam(a) && !this.hasHighlightedTeam(b)) {
			return -1;
		}

		if (this.hasHighlightedTeam(b) && !this.hasHighlightedTeam(a)) {
			return 1;
		}

		// if user proximity weighting has been applied in configuration, and the user's IP-derived latitude and longitude are available
		if (this._configurationObject.applyUserProximityWeighting && this.userRetrievedLat !== null && this.userRetrievedLon !== null) {

			// calculate distance from each team's location to the user, and check if the distance falls within the threshold to be considered "close"
			const aHomeTeamClose = typeof a.MatchData.HomeTeam !== "undefined" && typeof a.MatchData.HomeTeam.latitude !== "undefined" && typeof a.MatchData.HomeTeam.longitude !== "undefined"
				&& this.coordinateDistance(a.MatchData.HomeTeam.latitude, a.MatchData.HomeTeam.longitude, this.userRetrievedLat, this.userRetrievedLon) <= this.teamProximityThreshold;
			const aAwayTeamClose = typeof a.MatchData.AwayTeam !== "undefined" && typeof a.MatchData.AwayTeam.latitude !== "undefined" && typeof a.MatchData.AwayTeam.longitude !== "undefined"
				&& this.coordinateDistance(a.MatchData.AwayTeam.latitude, a.MatchData.AwayTeam.longitude, this.userRetrievedLat, this.userRetrievedLon) <= this.teamProximityThreshold;

			const bHomeTeamClose = typeof b.MatchData.HomeTeam !== "undefined" && typeof b.MatchData.HomeTeam.latitude !== "undefined" && typeof b.MatchData.HomeTeam.longitude !== "undefined"
				&& this.coordinateDistance(b.MatchData.HomeTeam.latitude, b.MatchData.HomeTeam.longitude, this.userRetrievedLat, this.userRetrievedLon) <= this.teamProximityThreshold;
			const bAwayTeamClose = typeof b.MatchData.AwayTeam !== "undefined" && typeof b.MatchData.AwayTeam.latitude !== "undefined" && typeof b.MatchData.AwayTeam.longitude !== "undefined"
				&& this.coordinateDistance(b.MatchData.AwayTeam.latitude, b.MatchData.AwayTeam.longitude, this.userRetrievedLat, this.userRetrievedLon) <= this.teamProximityThreshold;

			const aHasCloseTeam = aHomeTeamClose || aAwayTeamClose;
			const bHasCloseTeam = bHomeTeamClose || bAwayTeamClose;

			// if one game has at least one close team, but the other doesn't, put the game with the close team(s) ahead
			if (aHasCloseTeam && !bHasCloseTeam) {
				return -1;
			}

			if (bHasCloseTeam && !aHasCloseTeam) {
				return 1;
			}
		}

		// if one game is live/paused and the other is not, put the live game ahead
		if ((this.matchIsLive(a) || this.matchIsPaused(a)) && (!this.matchIsLive(b) && !this.matchIsPaused(b))) {
			return -1;
		}

		if ((this.matchIsLive(b) || this.matchIsPaused(b)) && (!this.matchIsLive(a) && !this.matchIsPaused(a))) {
			return 1;
		}

		// if one game is upcoming and the other is neither live nor upcoming, put the upcoming game first
		if (this.matchIsUpcoming(a) && !this.matchIsLive(b) && !this.matchIsPaused(b) && !this.matchIsUpcoming(b)) {
			return -1;
		}

		if (this.matchIsUpcoming(b) && !this.matchIsLive(a) && !this.matchIsPaused(a) && !this.matchIsUpcoming(a)) {
			return 1;
		}

		// if both games are live:
		if (this.matchIsLive(a) || this.matchIsPaused(a)) {
			const arbitrarySportOrder = ["NFL", "NBA", "MLB", "NHL", "EPL", "ESP"];
			// if games have different sports and one or both of them is in the priority list, put the one first in the list ahead
			if (a.MatchData.Sport !== b.MatchData.Sport) {
				if (arbitrarySportOrder.includes(a.MatchData.Sport) || arbitrarySportOrder.includes(b.MatchData.Sport)) {
					if (!arbitrarySportOrder.includes(a.MatchData.Sport)) {
						return 1;
					}
	
					if (!arbitrarySportOrder.includes(b.MatchData.Sport)) {
						return -1;
					}
	
					return arbitrarySportOrder.indexOf(a.MatchData.Sport) - arbitrarySportOrder.indexOf(b.MatchData.Sport);
				}
			}
		}


		// otherwise, put the game with an earlier start time first
		return compareAsc(a.MatchData.UserDate, b.MatchData.UserDate);
	}

	getGeoData(): Promise<void> {
		return new Promise((resolve) => {
			if (this._configurationObject.user) {
				if (this._configurationObject.user.country) {
					this.geoCountry = this._configurationObject.user.country;
				}

				if (this._configurationObject.user.state) {
					this.geoRegion = this._configurationObject.user.country;
				}

				if (this._configurationObject.user.latitude && this._configurationObject.user.longitude) {
					this.userRetrievedLat = this._configurationObject.user.latitude;
					this.userRetrievedLon = this._configurationObject.user.longitude;
				}
			}


			// if bookmaker country blacklist exists
			if (typeof this._configurationObject.bookmakerCountryBlacklist != "undefined") {
				// if user's country is in bookmaker country blacklist
				if (this.geoCountry && this._configurationObject.bookmakerCountryBlacklist.includes(this.geoCountry)) {

					// if bookmaker region whitelist doesn't exist, or doesn't have a list for the user's country,
					// or user's region isn't available, or the user's region isn't in the list for the user's country
					if (typeof this._configurationObject.bookmakerRegionWhitelist == "undefined"
						|| typeof this._configurationObject.bookmakerRegionWhitelist[this.geoCountry] == "undefined"
						|| !this.geoRegion
						|| !this._configurationObject.bookmakerRegionWhitelist[this.geoCountry].includes(this.geoRegion)) {
						
						// remove the bookmaker theme from the widget
						this._configurationObject.bookmakerTheme = "";
					}
				}
			} else
			// if bookmaker country whitelist exists
			if (typeof this._configurationObject.bookmakerCountryWhitelist != "undefined") {
				// if user's country is not in bookmaker country whitelist
				if (!this._configurationObject.bookmakerCountryWhitelist.includes(this.geoCountry)) {
					// remove bookmaker theme
					this._configurationObject.bookmakerTheme = "";
				} else
					
				
				// if bookmaker region blacklist exists, and user's country has record in bookmaker region blacklist,
				// and user's region is available, and user's region is in list for user's country
				if (typeof this._configurationObject.bookmakerRegionBlacklist != "undefined"
					&& typeof this._configurationObject.bookmakerRegionBlacklist[this.geoCountry] != "undefined"
					&& this.geoRegion
					&& this._configurationObject.bookmakerRegionBlacklist[this.geoCountry].includes(this.geoRegion)) {
					
					// remove bookmaker theme
					this._configurationObject.bookmakerTheme = "";
				}
			}
			resolve();
		});
	}
	
	convertBallsToOvers(balls: number, allowInt?: boolean): string {
		const fullOvers = Math.floor(balls / 6);
		const overBalls = balls % 6;
		if (allowInt && overBalls === 0) {
			return `${fullOvers}`;
		}
		return `${fullOvers}.${overBalls}`;
	}

	teamLostMatch(team: "home" | "away", match: Match): boolean {
		if (!this.matchIsOver(match)) {
			return false;
		}

		if (match.MatchData.Sport.toUpperCase() === "BBL") {
			if (team === "home") {
				return match.LiveData.winningTeam === match.MatchData.AwayTeam.Abv;
			} else {
				return match.LiveData.winningTeam === match.MatchData.HomeTeam.Abv;
			}
		}

		if (team === "home") {
			return match.LiveData.homeScore < match.LiveData.awayScore;
		} else {
			return match.LiveData.homeScore > match.LiveData.awayScore;
		}
	}

	matchDrawn(match: Match): boolean {
		if (!this.matchIsOver(match)) {
			return false;
		}

		if (match.MatchData.Sport.toUpperCase() === "BBL") {
			return match.LiveData.winningTeam !== match.MatchData.HomeTeam.Abv && match.LiveData.winningTeam !== match.MatchData.AwayTeam.Abv;
		}

		return match.LiveData.homeScore === match.LiveData.awayScore;
	}

	cricketScoreString(match: Match, team: "home" | "away"): string {
		if (team === "home") {
			if (typeof match.LiveData.homeWickets == "undefined" || typeof match.LiveData.homeRuns == "undefined"
				|| match.LiveData.homeRuns + match.LiveData.homeWickets + match.LiveData.homeBalls === 0) {
				return "";
			}
			return `${match.LiveData.homeWickets == 10 ? "" : `${match.LiveData.homeWickets}/`}${match.LiveData.homeRuns}`;
		}

		if (typeof match.LiveData.awayWickets == "undefined" || typeof match.LiveData.awayRuns == "undefined"
			|| match.LiveData.awayRuns + match.LiveData.awayWickets + match.LiveData.awayBalls === 0) {
			return "";
		}
		return `${match.LiveData.awayWickets == 10 ? "" : `${match.LiveData.awayWickets}/`}${match.LiveData.awayRuns}`;
		
	}

	matchFitsFilter(sport: string, maxHoursAhead?: number, maxHoursBehind?: number) {
		return (match: Match): boolean => match && match.MatchData && match.MatchData.Sport && sport
			&& (match.MatchData.Sport.toUpperCase() === sport.toUpperCase() || sport.toUpperCase() === "ALL")
			&& (this.matchFitsTimeFilter(match, maxHoursAhead, maxHoursBehind) || this.isHighlightedMatch(match));
	}

	matchFitsTimeFilter(match: Match, maxHoursAhead?: number, maxHoursBehind?: number) : boolean {
		return this.matchIsLive(match) 
		|| this.matchIsPaused(match)
		//return matchService.matchIsUpcoming(match) && differenceInHours(new Date(), match.MatchData.UserDate) > -72)
		|| (this.matchIsUpcoming(match) && (!maxHoursAhead || differenceInHours(new Date(), match.MatchData.UserDate) > -maxHoursAhead))
		//|| (matchService.matchIsOver(match) && differenceInHours(new Date(), match.MatchData.UserDate) < 24);
		|| (this.matchIsOver(match) && (!maxHoursBehind || differenceInHours(new Date(), match.MatchData.UserDate) < maxHoursBehind));
	}

	lostSet(match: Match, playerNo: number, setNo: number): boolean {
		const trailingSet = playerNo == 1
			? match.LiveData["p1Set" + setNo] < match.LiveData["p2Set" + setNo]
			: match.LiveData["p2Set" + setNo] < match.LiveData["p1Set" + setNo];
		const setOver = (match.LiveData.player1Sets + match.LiveData.player2Sets) >= setNo;

		return trailingSet && setOver;
	}

	lostTennisMatch(playerNo: number, match: Match): boolean {
		if (!this.matchIsOver(match)) {
			return false;
		}

		if (match.LiveData.status === "retirement") {
			if (playerNo === 1) {
				return match.LiveData.player1WinProb === 0;
			} else {
				return match.LiveData.player2WinProb === 0;
			}
		}

		if (playerNo === 1) {
			return match.LiveData.player1Sets < match.LiveData.player2Sets;
		} else {
			return match.LiveData.player2Sets < match.LiveData.player1Sets;
		}
	}

	formattedDate(date: Date): string {
		if (date) {
			// if date is exactly on the hour (local time)
			if (getMinutes(date) === 0) {
				// remove the ":00" from the time section
				if (this._configurationObject.dateDayBeforeMonth) {
					return format(date, "ha, d MMM");
				} else {
					return format(date, "ha, MMM d");
				}
				
			}

			if (this._configurationObject.dateDayBeforeMonth) {
				return format(date, "h:mma, d MMM");
			} else {
				return format(date, "h:mma, MMM d");
			}
		}

		return "";
		
	}

	formattedDateAlt(date: Date): string {
		if (date) {
			// if date is exactly on the hour (local time)
			if (getMinutes(date) === 0) {
				// remove the ":00" from the time section
				if (this._configurationObject.dateDayBeforeMonth) {
					return format(date, "d MMM, ha");
				} else {
					return format(date, "MMM d, ha");
				}
			}

			if (this._configurationObject.dateDayBeforeMonth) {
				return format(date, "d MMM, h:mma");
			} else {
				return format(date, "MMM d, h:mma");
			}
		}
		

		return "";
	}

	matchStartCountdown(match: Match): string {
		if (match && match.MatchData.UserDate) {
			const hoursleft = differenceInMilliseconds(match.MatchData.UserDate, new Date()) / (1000 * 3600);
			if (hoursleft < 0) {
				return "Soon";
			} else if (hoursleft < 1) {
				// minutes remaining
				const minutesleft = hoursleft * 60;
				return minutesleft.toFixed(0) + (minutesleft.toFixed(0) == "1" ? " min" : " mins");
			} else if (hoursleft < 24) {
				// hours remaining
				return hoursleft.toFixed(0) + (hoursleft.toFixed(0) == "1" ? " hr" : " hrs");
			} else {
				// days remaining
				const daysleft = hoursleft / 24;
				return daysleft.toFixed(0) + (daysleft.toFixed(0) == "1" ? " day" : " days");
			}
		}
		
		return "";
	}

	teamInRedZone(data: MatchLiveData): boolean {
		return data && data.actionSideSI && data.actionYardLine && data.teamWithBallSI
			&& data.actionYardLine <= 20 && data.actionSideSI !== data.teamWithBallSI;
	}

	fieldDiagramBallLocation (match: Match): number {
		if (match.LiveData && match.LiveData.actionYardLine !== null && match.LiveData.actionSideSI !== null
			&& typeof match.LiveData.actionYardLine != "undefined" && typeof match.LiveData.actionSideSI != "undefined") {
			const startingPoint = 2132;
			const displacement = (50 - match.LiveData.actionYardLine) * 36;
			if (match.LiveData.actionSideSI == match.MatchData.HomeTeam.Abv) {
				return startingPoint - displacement;
			} else {
				return startingPoint + displacement;
			}
		} else {
			return -500;
		}
	}

	minUniqueName(match: Match, playerNo: number, forceFirstInitial: boolean): string {
		if (!match) {
			return "";
		}
		if (match.MatchData.PlayerData.player1.nameDetails.last !== match.MatchData.PlayerData.player2.nameDetails.last
			&& !forceFirstInitial) {
			return match.MatchData.PlayerData[`player${playerNo}`].nameDetails.last;
		}

		if (match.MatchData.PlayerData.player1.nameDetails.last !== match.MatchData.PlayerData.player2.nameDetails.last
			|| match.MatchData.PlayerData.player1.nameDetails.first[0] !== match.MatchData.PlayerData.player1.nameDetails.first[0]) {
			return match.MatchData.PlayerData[`player${playerNo}`].nameDetails.first[0] + ". " + match.MatchData.PlayerData[`player${playerNo}`].nameDetails.last;
		}

		return match.MatchData.PlayerData[`player${playerNo}`].nameDetails.first + " " + match.MatchData.PlayerData[`player${playerNo}`].nameDetails.last;
	}

	downAndDistanceSummary(data: MatchLiveData): string {
		if (data.actionDown === null || data.yardsToFirstDown === null
			|| typeof data.actionDown === "undefined" || typeof data.yardsToFirstDown === "undefined") {
			
			if (data.status == "completed" || data.status == "ended" || data.status == "retirement") {
				return "-";
			} else {
				return "-";
			}
		}
		const down = this._nthFilter(data.actionDown);

		return `${down} and ${data.yardsToFirstDown}`;
	}

	liveStatusString(match: Match): string {
		const data = match.LiveData;

		if (data.status === "halftime") {
			return "Halftime";
		}

		if (match.MatchData.Sport.toLowerCase() == "ten") {
			const currentSet = data.player1Sets + data.player2Sets + 1;
			return `${this._nthFilter(currentSet)} Set`;
		}

		if (match.MatchData.Sport.toLowerCase() == "afl") {
			return `Q${data.period} ${Math.floor(data.periodSeconds / 60)}:${
				data.periodSeconds % 60 < 10 ? "0" : ""
			}${
				data.periodSeconds % 60
			}`;
		}

		if (match.MatchData.Sport.toLowerCase() == "bbl") {
			let ballCount: number, ballQuota: number;
			if (data.battingTeam == match.MatchData.HomeTeam.Abv) {
				ballCount = data.homeBalls;
				ballQuota = data.homeBallQuota;
			} else {
				ballCount = data.awayBalls;
				ballQuota = data.awayBallQuota;
			}
			return  `${this._nthFilter(data.inningsNo)} | ${this.convertBallsToOvers(ballCount)}${ballQuota !== 120 ? `/${this.convertBallsToOvers(ballQuota, true)}` : ""}`;
		}

		if (["nrl", "soo"].includes(match.MatchData.Sport.toLowerCase())) {
			let periodAbv;
			if (data.minsGone <= 40) {
				periodAbv = "H1";
			} else if (data.minsGone <= 80) {
				periodAbv = "H2";
			} else {
				periodAbv = "ET";
			}
			return `${periodAbv} ${Math.floor(data.minsGone)}:${
				Math.floor((data.minsGone % 1) * 60) < 10 ? "0" : ""
			}${
				Math.floor((data.minsGone % 1) * 60)
			}`;
		}

		if (match.MatchData.Sport.toLowerCase() == "cfb") {
			if (data.period > 4) {
				// Display just the overtime period
				return `OT${data.period - 4}`;
			}

			const quarterSecondsLeft = 900 - data.periodSeconds;

			return `${this._nthFilter(data.period)} ${Math.floor(quarterSecondsLeft / 60)}:${quarterSecondsLeft % 60 < 10 ? "0" : ""}${quarterSecondsLeft % 60}`;
		}

		if (match.MatchData.Sport.toLowerCase() == "nfl") {
			let periodAbv;
			if (data.period > 4) {
				periodAbv = `${data.period == 5 ? "" : data.period - 4}OT`;
			} else {
				periodAbv = `${this._nthFilter(data.period)}`;
			}

			const quarterSecondsLeft = 900 - data.periodSeconds;

			return `${periodAbv} ${Math.floor(quarterSecondsLeft / 60)}:${quarterSecondsLeft % 60 < 10 ? "0" : ""}${quarterSecondsLeft % 60}`;
		}

		if (match.MatchData.Sport.toLowerCase() == "nba") {
			let periodAbv;
			if (data.period > 4) {
				periodAbv = `${data.period == 5 ? "" : data.period - 4}OT`;
			} else {
				periodAbv = `${this._nthFilter(data.period)}`;
			}

			const periodSecondsLeft = (data.period > 4 ? 300 : 720) - data.periodSeconds;

			return `${periodAbv} ${Math.floor(periodSecondsLeft / 60)}:${periodSecondsLeft % 60 < 10 ? "0" : ""}${periodSecondsLeft % 60}`;
		}
		
		if (match.MatchData.Sport.toLowerCase() == "cbb") {
			let periodAbv;
			if (data.period > 2) {
				periodAbv = `${data.period == 3 ? "" : data.period - 2}OT`;
			} else {
				periodAbv = `${this._nthFilter(data.period)}`;
			}

			const periodSecondsLeft = (data.period > 2 ? 300 : 1200) - data.periodSeconds;

			return `${periodAbv} ${Math.floor(periodSecondsLeft / 60)}:${periodSecondsLeft % 60 < 10 ? "0" : ""}${periodSecondsLeft % 60}`;
		}

		if (match.MatchData.Sport.toLowerCase() == "mlb") {
			return `${data.inningHalf === "B" ? "Bot" : "Top"} ${this._nthFilter(data.inning)}`;
		}

		if (match.MatchData.Sport.toLowerCase() == "nhl") {
			if (data.period === 5 && data.match_type === "REG") {
				return "Shootout";
			}
			let periodAbv: string;
			if (data.period > 3) {
				periodAbv = `${data.period == 4 ? "" : data.period - 3}OT`;
			} else {
				periodAbv = `${this._nthFilter(data.period)}`;
			}

			//const quarterSecondsLeft = 1200 - data.periodSeconds;

			return `${periodAbv} ${data.clock}`;
		}

		if (this._generalService.isSoccer(match.MatchData.Sport)) {
			if (data.clock) {
				return data.clock;
			}

			return Math.floor(data.minsGone) + "'";
		}
		

		// TODO other sports
		return "";
	}

}